/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { WorkspaceLogoUpload } from "./WorkspaceLogoUpload";
import { useProductById } from "@/shared/hooks/api/useWorkspace";
import { normalizeUrl } from "@/shared/utils/urlHelper";
import * as z from "zod";
import { MagicFillButton, MagicFillButtonHandle } from "@/shared/components/form/MagicFillButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { Info } from "lucide-react";
import { InputWithMagicFill } from "@/shared/components/form/InputWithMagicFill";
import { TextareaWithMagicFill } from "@/shared/components/form/TextareaWithMagicFill";
import { getWorkspaceFormMetadata } from "@/shared/utils/magicFillMetadataHelpers";
import { MagicFillProvider } from "@/shared/contexts/MagicFillContext";
import { logger } from "@/infrastructure/lib/logger";

const productFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase, alphanumeric, separated by hyphens")
    .optional()
    .or(z.literal("")),
  description: z.string().optional(),
  website: z.string().url().optional().or(z.literal("")),
  logo_url: z.string().optional(),
});

interface ProductFormProps {
  workspaceId?: string;
  onFormDataChange?: (data: z.infer<typeof productFormSchema> & { logoFile?: File }) => void;
  hideSubmitButton?: boolean;
  hideMagicFillButton?: boolean;
  magicFillSize?: "sm" | "default" | "icon";
  initialValues?: z.infer<typeof productFormSchema> & { logoFile?: File };
}

export const WorkspaceForm = ({ workspaceId, onFormDataChange, hideSubmitButton, hideMagicFillButton, magicFillSize, initialValues }: ProductFormProps) => {
  const { t } = useTranslation();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [logoUpdateCounter, setLogoUpdateCounter] = useState<number>(0);
  const [hasTriggeredMagicFill, setHasTriggeredMagicFill] = useState(false);
  const magicFillButtonRef = useRef<MagicFillButtonHandle>(null);

  const form = useForm<z.infer<typeof productFormSchema>>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      website: "",
      logo_url: "",
    },
    mode: "onChange",
  });

  // Carregar dados do produto se estiver editando
  const { data: workspace } = useProductById(workspaceId);

  // Atualizar o formulário quando os dados do produto forem carregados ou quando receber valores iniciais
  useEffect(() => {
    if (!isFormInitialized) {
      let formValues;
      
      if (workspace) {
        // Se o produto existe (edição), use os dados do produto
        logger.debug('Filling workspace form with workspace data', { workspaceId, workspace });
        formValues = {
          name: workspace.name || "",
          slug: (workspace as any).slug || "",
          description: workspace.description || "",
          website: workspace.website || "",
          logo_url: workspace.logo_url || "",
        };
      } else if (initialValues) {
        // Se não tem produto mas tem valores iniciais, use esses valores
        logger.debug('Filling workspace form with initial values', { workspaceId, initialValues });
        formValues = {
          name: initialValues.name || "",
          slug: (initialValues as any).slug || "",
          description: initialValues.description || "",
          website: initialValues.website || "",
          logo_url: initialValues.logo_url || "",
        };
        
        // Se tiver um arquivo de logo nos valores iniciais
        if (initialValues.logoFile) {
          setLogoFile(initialValues.logoFile as File);
        }
      }
      
      if (formValues) {
        form.reset(formValues);
        setIsFormInitialized(true);
        
        // Notificar o componente pai sobre os valores iniciais
        if (onFormDataChange) {
          onFormDataChange({
            ...formValues,
            logoFile: logoFile || undefined,
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, initialValues, form, onFormDataChange, logoFile, isFormInitialized]);

  // Monitorar alterações em campos individuais
  const handleFieldChange = (name: keyof z.infer<typeof productFormSchema>, value: any) => {
    logger.debug('Workspace form field changed', { workspaceId, field: name, value });
    
    // Atualizar o estado interno do formulário
    form.setValue(name, value, { shouldValidate: true });
    
    // Notificar o componente pai sobre a alteração
    if (onFormDataChange) {
      const currentValues = form.getValues();
      onFormDataChange({
        ...currentValues,
        [name]: value,
        logoFile: logoFile || undefined,
      });
    }
  };

  // Função para lidar com a atualização da URL do logo após o upload
  const handleLogoUpdate = (url: string, file?: File) => {
    logger.debug('Workspace logo updated', { workspaceId, url, hasFile: !!file });
    
    // Atualizar o campo logo_url no formulário
    form.setValue("logo_url", url, { shouldValidate: true });
    
    // Atualizar o estado de preview
    setPreviewUrl(url);
    
    // Se recebemos um arquivo (para novos produtos), armazená-lo
    if (file) {
      setLogoFile(file);
    }
    
    // Incrementar o contador para forçar re-renderização do componente de logo
    setLogoUpdateCounter(prev => prev + 1);
    
    // Notificar o componente pai sobre a alteração
    if (onFormDataChange) {
      const currentValues = form.getValues();
      const logoFileToUse = file || logoFile || undefined;
      
      
      onFormDataChange({
        ...currentValues,
        logo_url: url,
        logoFile: logoFileToUse 
      });
    }
  };
  
  // Remover função não utilizada para evitar warnings

  // Função para lidar com a submissão do formulário
  const onSubmit = (data: z.infer<typeof productFormSchema>) => {
    logger.info('Workspace form submitted', { workspaceId });
    if (onFormDataChange) {
      onFormDataChange({
        ...data,
        logoFile: logoFile || undefined,
      });
    }
  };

  return (
    <MagicFillProvider>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 px-4">
          <div className="flex items-start justify-between gap-4">
            <WorkspaceLogoUpload
              workspaceId={workspaceId}
              organizationId={workspace?.organization_id || ''}
              currentLogoUrl={previewUrl || form.watch("logo_url") || null}
              key={`logo-upload-${workspaceId || 'new'}-${logoUpdateCounter}`} /* Usar o contador para forçar re-renderização */
              onLogoUpdate={(url, file) => handleLogoUpdate(url, file)}
            />
            {!hideMagicFillButton && !workspaceId && (
              <MagicFillButton
                ref={magicFillButtonRef}
                formType="product"
                size={magicFillSize || "icon"}
                getCurrentValues={() => form.getValues()}
                metadata={{
                  workspaceId,
                  fieldMetadata: getWorkspaceFormMetadata()
                }}
                onLoadingChange={() => { /* intentional: parent does not track loading here */ }}
                onApplySuggestions={(suggested) => {
                  const currentValues = form.getValues();
                  Object.entries(suggested).forEach(([key, value]) => {
                    if (Object.prototype.hasOwnProperty.call(currentValues, key)) {
                      form.setValue(key as any, value as any, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }
                  });
                  if (onFormDataChange) {
                    const updated = form.getValues();
                    onFormDataChange({
                      ...updated,
                      logoFile: logoFile || undefined,
                    });
                  }
                }}
                showClearButton={false}
              />
            )}
          </div>
        
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>{t("settings.workspaces.fields.name")}</FormLabel>
              <FormControl>
                <InputWithMagicFill
                  placeholder={t("settings.workspaces.fields.namePlaceholder")}
                  {...field}
                  formType="product"
                  fieldKey="name"
                  getCurrentValues={() => form.getValues()}
                  metadata={{ workspaceId }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFieldChange('name', e.target.value);
                    // Resetar flag quando o campo for limpo
                    if (!e.target.value?.trim() && hasTriggeredMagicFill) {
                      setHasTriggeredMagicFill(false);
                    }
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    const nameValue = e.target.value?.trim();
                    // Auto-generate slug from name
                    if (nameValue) {
                      const slug = nameValue
                        .toLowerCase()
                        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      form.setValue('slug', slug, { shouldValidate: true, shouldDirty: true });
                      handleFieldChange('slug', slug);
                    }
                    // Auto-trigger Magic Fill ao sair do campo nome (apenas uma vez)
                    if (nameValue && !hasTriggeredMagicFill) {
                      setTimeout(() => {
                        magicFillButtonRef.current?.triggerMagicFill();
                        setHasTriggeredMagicFill(true);
                      }, 300);
                    }
                  }}
                  onMagicFillApply={(value) => {
                    form.setValue("name", value, { shouldValidate: true, shouldDirty: true });
                    if (onFormDataChange) {
                      onFormDataChange({ ...form.getValues(), name: value, logoFile: logoFile || undefined });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
              {form.watch('slug') && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span>{t("settings.workspaces.fields.slug")}: <span className="font-mono">{form.watch('slug')}</span></span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-[250px]">{t("settings.workspaces.fields.slugHelper")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>{t("settings.workspaces.fields.description")}</FormLabel>
              <FormControl>
                <TextareaWithMagicFill
                  placeholder={t("settings.workspaces.fields.descriptionPlaceholder")}
                  {...field}
                  rows={6}
                  formType="product"
                  fieldKey="description"
                  getCurrentValues={() => form.getValues()}
                  metadata={{ workspaceId }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFieldChange('description', e.target.value);
                  }}
                  onMagicFillApply={(value) => {
                    form.setValue("description", value, { shouldValidate: true, shouldDirty: true });
                    if (onFormDataChange) {
                      onFormDataChange({ ...form.getValues(), description: value, logoFile: logoFile || undefined });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>{t("settings.workspaces.fields.website")}</FormLabel>
              <FormControl>
                <InputWithMagicFill
                  type="text"
                  placeholder="example.com"
                  {...field}
                  formType="product"
                  fieldKey="website"
                  getCurrentValues={() => form.getValues()}
                  metadata={{ workspaceId }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFieldChange('website', e.target.value);
                  }}
                  onBlur={(e) => {
                    const normalized = normalizeUrl(e.target.value);
                    field.onChange(normalized);
                    handleFieldChange('website', normalized);
                    field.onBlur();
                  }}
                  onMagicFillApply={(value) => {
                    const normalized = normalizeUrl(value);
                    form.setValue("website", normalized, { shouldValidate: true, shouldDirty: true });
                    if (onFormDataChange) {
                      onFormDataChange({ ...form.getValues(), website: normalized, logoFile: logoFile || undefined });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {!hideSubmitButton && (
          <Button type="submit">
            {t("common.save")}
          </Button>
        )}
      </form>
    </Form>
    </MagicFillProvider>
  );
};
